import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";

import { woocommerceStoreService } from "../services/woocommerceStoreService";
import type { WooOrder, WooPixPaymentDetails, WooStoreConfig } from "../types";
import { isPaidOrder } from "../utils/orderHelpers";
import {
  extractPixPaymentFromOrder,
  hasActivePixQrCode,
} from "../utils/pixPaymentHelpers";
import {
  openPaymentPopup,
  openPaymentPopupWindow,
} from "../utils/openIdLogin";

const PAYMENT_POLL_INTERVAL_MS = 5000;
const MAX_POLLS_WITHOUT_POPUP = 12;
const MAX_MONITORING_MS = 15 * 60 * 1000;

interface PixPaymentSession {
  order: WooOrder;
  details: WooPixPaymentDetails;
}

type PollResult = "paid" | "pix" | "unpaid" | "timeout" | "cancelled";

interface UseWooCommercePaymentMonitorOptions {
  store: WooStoreConfig | null;
  resolveStore?: (order: WooOrder) => WooStoreConfig | null;
  onOrdersRefresh: (customerId?: number) => Promise<unknown>;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function isPopupClosed(popup: Window | null): boolean {
  return !popup || popup.closed;
}

/** Monitora popup ou QR PIX inline + polling até confirmação. */
export function useWooCommercePaymentMonitor({
  store,
  resolveStore,
  onOrdersRefresh,
}: UseWooCommercePaymentMonitorOptions) {
  const [monitoringOrderId, setMonitoringOrderId] = useState<number | null>(
    null,
  );
  const [successOrder, setSuccessOrder] = useState<WooOrder | null>(null);
  const [pixPayment, setPixPayment] = useState<PixPaymentSession | null>(null);
  const monitorCancelRef = useRef(false);

  const stopMonitoring = useCallback(() => {
    monitorCancelRef.current = true;
  }, []);

  const closePixPayment = useCallback(() => {
    stopMonitoring();
    setPixPayment(null);
    setMonitoringOrderId(null);
    toast.info(
      "Pagamento PIX pendente. Você pode retomar em Meus pedidos.",
    );
  }, [stopMonitoring]);

  const pollUntilResolved = useCallback(
    async (
      activeStore: WooStoreConfig,
      order: WooOrder,
      options?: {
        popup?: Window | null;
        switchToPixOnPopupClose?: boolean;
      },
    ): Promise<PollResult> => {
      const startedAt = Date.now();
      let pollsWithoutPopup = 0;
      const popup = options?.popup ?? null;
      const hasPopupRef = popup !== null;
      monitorCancelRef.current = false;

      while (!monitorCancelRef.current) {
        if (Date.now() - startedAt >= MAX_MONITORING_MS) {
          return "timeout";
        }

        if (hasPopupRef && isPopupClosed(popup)) {
          const updated = await woocommerceStoreService.getOrder(
            activeStore,
            order.id,
          );
          await onOrdersRefresh(order.customerId);

          if (isPaidOrder(updated)) {
            return "paid";
          }

          if (
            options?.switchToPixOnPopupClose &&
            hasActivePixQrCode(updated)
          ) {
            const details = extractPixPaymentFromOrder(updated);
            if (details) {
              setPixPayment({ order: updated, details });
              return "pix";
            }
          }

          return "unpaid";
        }

        const updated = await woocommerceStoreService.getOrder(
          activeStore,
          order.id,
        );
        await onOrdersRefresh(order.customerId);

        if (isPaidOrder(updated)) {
          return "paid";
        }

        if (!hasPopupRef && hasActivePixQrCode(updated)) {
          const details = extractPixPaymentFromOrder(updated);
          if (details) {
            setPixPayment((current) =>
              current ?? { order: updated, details },
            );
          }
        }

        if (!hasPopupRef && !hasActivePixQrCode(updated)) {
          pollsWithoutPopup += 1;
          if (pollsWithoutPopup >= MAX_POLLS_WITHOUT_POPUP) {
            return "timeout";
          }
        }

        await sleep(PAYMENT_POLL_INTERVAL_MS);
      }

      return "cancelled";
    },
    [onOrdersRefresh],
  );

  const payOrder = useCallback(
    async (order: WooOrder) => {
      const activeStore = resolveStore?.(order) ?? store;
      if (!activeStore) return;

      setMonitoringOrderId(order.id);
      monitorCancelRef.current = false;

      const finishUnpaid = (message: string) => {
        toast.info(message);
        setMonitoringOrderId(null);
      };

      const markPaid = async () => {
        const paid = await woocommerceStoreService.getOrder(
          activeStore,
          order.id,
        );
        setPixPayment(null);
        setSuccessOrder(paid);
        setMonitoringOrderId(null);
      };

      try {
        const freshOrder = await woocommerceStoreService.getOrder(
          activeStore,
          order.id,
        );

        if (hasActivePixQrCode(freshOrder)) {
          const details = extractPixPaymentFromOrder(freshOrder);
          if (!details) {
            toast.error("Não foi possível carregar o QR Code PIX.");
            setMonitoringOrderId(null);
            return;
          }

          setPixPayment({ order: freshOrder, details });
          const result = await pollUntilResolved(activeStore, freshOrder);
          if (result === "paid") {
            await markPaid();
          }
          return;
        }

        const popup = openPaymentPopupWindow(activeStore, freshOrder.paymentUrl);
        if (!popup) {
          openPaymentPopup(activeStore, freshOrder.paymentUrl);
        }

        const popupResult = await pollUntilResolved(activeStore, freshOrder, {
          popup,
          switchToPixOnPopupClose: true,
        });

        if (popupResult === "paid") {
          await markPaid();
          return;
        }

        if (popupResult === "pix") {
          const pixResult = await pollUntilResolved(activeStore, freshOrder);
          if (pixResult === "paid") {
            await markPaid();
          }
          return;
        }

        if (popupResult === "timeout") {
          finishUnpaid(
            "Pagamento não concluído. Você pode tentar novamente em Meus pedidos.",
          );
          return;
        }

        finishUnpaid(
          "Pagamento não concluído. Você pode tentar novamente em Meus pedidos.",
        );
      } catch {
        toast.error("Não foi possível verificar o pagamento.");
        setPixPayment(null);
        setMonitoringOrderId(null);
      }
    },
    [store, resolveStore, pollUntilResolved],
  );

  const clearSuccessOrder = useCallback(() => {
    setSuccessOrder(null);
  }, []);

  return {
    payOrder,
    monitoringOrderId,
    successOrder,
    clearSuccessOrder,
    pixPayment,
    closePixPayment,
    isMonitoring: monitoringOrderId !== null,
  };
};
